// Plain read-only interfaces for the email campaign send/report API - not MibeModel
// classes, since these payloads are never edited/serialized back by the client.

export type CampaignBccPolicy = 'EVERY_MESSAGE' | 'FIRST_MESSAGE_ONLY' | 'SEPARATE_SUMMARY';

export type CampaignLifecycleStatus =
  | 'draft'
  | 'prepared'
  | 'queued'
  | 'sending'
  | 'partially_failed'
  | 'completed'
  | 'failed'
  | 'cancelled';

export function campaignIsEditable(status: CampaignLifecycleStatus): boolean {
  return status === 'draft' || status === 'prepared';
}

export interface CampaignStatusCounts {
  intended: number;
  queued: number;
  started: number;
  accepted: number;
  failed: number;
  retrying: number;
  unknown: number;
  cancelled: number;
  processed: number;
  delivered: number | null;
  bounced: number | null;
}

export interface CampaignStatus {
  campaign_id: string;
  status: CampaignLifecycleStatus;
  phase: CampaignLifecycleStatus;
  is_terminal: boolean;
  progress_percent: number;
  progress_denominator: 'intended_recipient_count';
  counts: CampaignStatusCounts;
  bcc_policy: CampaignBccPolicy;
  started_at?: string | null;
  completed_at?: string | null;
  last_error?: string | null;
  last_updated_at: string;
  recommended_poll_interval_ms: number;
  warnings: string[];
}

// Emitted by EmailCampaignService.pollStatus on every tick. A failed poll (network blip,
// transient 5xx, etc.) no longer kills the polling loop - it keeps the last known status
// and reports the failure here so the UI can say "still checking" instead of going silent.
export interface CampaignStatusPoll {
  status: CampaignStatus;
  pollError: string | null;
  // True once too many consecutive polls have failed - polling has stopped and the UI
  // should say so plainly instead of implying the check is still in progress forever.
  pollFailed: boolean;
}

export interface CampaignSummary {
  campaign_id: string;
  subject: string;
  from_email: string;
  body_template?: string;
  playlists?: CampaignPlaylistSnapshot[];
  status: CampaignLifecycleStatus;
  bcc_policy: CampaignBccPolicy;
  intended_recipient_count: number;
  accepted_recipient_count: number;
  delivered_recipient_count: number | null;
  progress_percent: number;
  created_at: string;
  last_updated_at: string;
  created_by?: CampaignCreator | string | null;
  // Groups actually sent to live under metadata.source_groups, not top-level.
  metadata?: {
    source_groups?: CampaignSourceGroup[];
  };
}

export interface CampaignPlaylistSnapshot {
  playlist_id: number;
  current_playlist_id?: number | null;
  playlist_name: string;
  add_to_account: boolean;
  added_at?: string;
}

export interface CampaignRecipientRow {
  id: number;
  recipient_email: string;
  recipient_type: 'client' | 'composer' | 'staff' | 'external';
  sequence_number: number;
  status: string;
  bcc_intended: boolean;
  celery_task_id: string;
  attempt_count: number;
  accepted_at: string | null;
  delivered_at: string | null;
  last_error_message: string;
}

export interface CampaignAttempt {
  id: number;
  recipient: number;
  status: string;
  created_at: string;
  error_message?: string | null;
}

export interface CampaignSourceGroup {
  id: number;
  name: string;
}

// Shape of the creator varies by endpoint (id-only, username, or full name parts) -
// getCampaignCreatorName() below picks whatever's usable.
export interface CampaignCreator {
  id?: number;
  username?: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  name?: string;
}

export function getCampaignCreatorName(creator: CampaignCreator | string | null | undefined): string {
  if (!creator) {
    return '—';
  }
  if (typeof creator === 'string') {
    return creator;
  }
  let fullName = [creator.first_name, creator.last_name].filter((p) => !!p).join(' ');
  return creator.name || fullName || creator.username || creator.email || (creator.id ? `User #${creator.id}` : '—');
}

export interface CampaignPreviewRequest {
  template_id?: number;
  recipient_group_ids?: number[];
  explicit_recipient_emails?: string[];
  bcc?: string[];
  bcc_policy: CampaignBccPolicy;
}

export interface CampaignPreviewResponse {
  resolved_recipient_count: number;
  deduplicated_recipient_count: number;
  excluded_invalid_addresses: string[];
  source_groups: CampaignSourceGroup[];
  bcc_policy: CampaignBccPolicy;
  bcc_policy_effect: string;
  invalid_bcc_addresses: string[];
  warnings: string[];
}

export interface CampaignCreateRequest {
  template_id?: number;
  from_email: string;
  subject: string;
  body: string;
  recipient_group_ids?: number[];
  explicit_recipient_emails?: string[];
  cc?: string[];
  bcc?: string[];
  bcc_policy: CampaignBccPolicy;
  idempotency_key: string;
  auto_send: boolean;
  included_playlist_ids?: number[];
  // Playlists to add directly to each recipient's account, distinct from the playlist
  // links embedded in the message body.
  recipient_playlist_ids?: number[];
}

export interface CampaignCreateResponse {
  campaign_id: string;
  status: CampaignLifecycleStatus;
  intended_recipient_count: number;
  queued_recipient_count: number;
  bcc_policy: CampaignBccPolicy;
  duplicate: boolean;
  auto_send: boolean;
  created_at: string;
  status_payload: Partial<CampaignStatus>;
}

export interface CampaignSendResponse {
  duplicate: boolean;
  status_payload: CampaignStatus;
}

export interface CampaignReconciliation {
  intended: number;
  queued: number;
  started: number;
  accepted: number;
  failed: number;
  unknown: number;
  provider_discrepancies: any[];
}

export interface GetCampaignRecipientsOptions {
  status?: string;
  recipient_email?: string;
  recipient_type?: string;
  bcc_intended?: boolean;
  limit?: number;
  offset?: number;
  page?: number;
}

export interface GetCampaignAttemptsOptions {
  status?: string;
  recipient?: number;
  limit?: number;
  offset?: number;
  page?: number;
}
